<template>
  <div class="dashboard-body-futuristic">
    <main class="futuristic-container" style="flex-direction: column; padding: 1rem 2rem;">
      <section class="dashboard-card-futuristic dashboard-card-scrollable" style="width: 100%;">
        <div class="pdf-viewer-wrapper">

          <Teleport to="#page-action-bar">
            <button class="btn-viewer-action btn-back" @click="goBack">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back
            </button>
            <h1 class="page-bar-title">Obligations</h1>
            <div class="page-bar-actions">
              <div class="pdf-search-wrapper">
                <svg class="pdf-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  v-model="searchQuery"
                  type="text"
                  class="pdf-search-input"
                  placeholder="Search obligations..."
                />
                <button v-if="searchQuery" class="pdf-search-clear" @click="searchQuery = ''" title="Clear search" aria-label="Clear search">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <button class="btn-viewer-action btn-download" @click="downloadPdf" :disabled="isDownloading" :class="{ 'disabled': isDownloading }">
                <svg v-if="!isDownloading" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <svg v-else class="spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                {{ isDownloading ? 'Downloading...' : 'Download PDF' }}
              </button>
            </div>
          </Teleport>

          <!-- Contractor + Client obligation sections -->
          <template v-for="section in sections" :key="section.key">
            <div class="oblig-section-head" :class="section.key">
              <span class="oblig-dot"></span>
              {{ section.label }}
              <span class="oblig-count">{{ section.rows.length }}</span>
            </div>
            <div class="pdf-content-container table-responsive" style="margin-bottom: 1.5rem;">
              <table class="pdf-content-table">
                <colgroup>
                  <col style="width: 6%;">   <!-- Sr -->
                  <col style="width: 60%;">  <!-- Obligation -->
                  <col style="width: 18%;">  <!-- Due Date -->
                  <col style="width: 16%;">  <!-- Reference -->
                </colgroup>
                <thead>
                  <tr>
                    <th>Sr</th>
                    <th>Obligation</th>
                    <th>Due Date</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in section.rows" :key="row.id">
                    <td>{{ i + 1 }}</td>
                    <td><div class="cell-scroll" v-html="highlight(row.obligation)"></div></td>
                    <td>
                      <span v-if="row.due_date" class="due-badge">{{ formatDate(row.due_date) }}</span>
                      <span v-else class="due-none">{{ row.deadline_expr || '—' }}</span>
                    </td>
                    <td><div class="cell-scroll" v-html="highlight(row.reference)"></div></td>
                  </tr>
                  <tr v-if="!section.rows.length">
                    <td colspan="4" style="text-align:center; padding:1.5rem; color:#64748b;">
                      <template v-if="searchQuery">No matches in {{ section.label }}.</template>
                      <template v-else>No {{ section.label.toLowerCase() }} extracted yet.</template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

        </div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import { notify } from '@/utils/notify';

export default {
  name: "ObligationsView",

  props: {
    getAccessToken: Function,
  },

  data() {
    return {
      contractor: [],
      client: [],
      searchQuery: '',
      isDownloading: false,
    };
  },

  computed: {
    // Each section filtered by the search query (matches obligation or reference).
    sections() {
      const q = this.searchQuery.trim().toLowerCase();
      const filt = (rows) => {
        if (!q) return rows;
        return rows.filter(r =>
          [r.obligation, r.reference, r.deadline_expr, r.due_date]
            .some(v => String(v || '').toLowerCase().includes(q))
        );
      };
      return [
        { key: 'contractor', label: 'Contractor Obligations', rows: filt(this.contractor) },
        { key: 'client', label: 'Client Obligations', rows: filt(this.client) },
      ];
    },
  },

  mounted() {
    this.fetchObligations();
  },

  methods: {
    getAuthHeaders() {
      return { Authorization: 'Bearer ' + this.getAccessToken() };
    },

    goBack() {
      // Back to the bid's Analysis tab; carry status/from so the sidebar keeps its highlight.
      const { status, from } = this.$route.query;
      this.$router.push({
        name: 'BidDetails',
        params: { pdf_id: this.$route.params.pdf_id },
        query: { tab: 'analysis', ...(status ? { status } : {}), ...(from ? { from } : {}) },
      });
    },

    formatDate(d) {
      if (!d) return '—';
      // API returns YYYY-MM-DD; show DD-MM-YYYY.
      const [y, m, day] = String(d).slice(0, 10).split('-');
      return (y && m && day) ? `${day}-${m}-${y}` : d;
    },

    fetchObligations() {
      const pdf_id = this.$route.params.pdf_id;
      if (!pdf_id) return;
      axios.get(this.$API_URL + `/user/obligations?pdf_id=${pdf_id}`, {
        headers: this.getAuthHeaders()
      })
        .then(res => {
          this.contractor = res.data.contractor || [];
          this.client = res.data.client || [];
        })
        .catch(err => console.error(err));
    },

    downloadPdf() {
      // Obligations only, styled like obligation_formate.pdf
      return this.downloadFile('/user/download/obligations-pdf', 'obligations.pdf', 'isDownloading');
    },

    async downloadFile(endpoint, fallbackName, spinnerFlag) {
      if (this[spinnerFlag]) return;
      this[spinnerFlag] = true;
      try {
        const pdf_id = this.$route.params.pdf_id;
        const res = await axios.get(
          this.$API_URL + `${endpoint}?pdf_id=${pdf_id}`,
          { responseType: 'blob', headers: this.getAuthHeaders() }
        );

        let filename = fallbackName;
        const disp = res.headers['content-disposition'] || res.headers['Content-Disposition'];
        if (disp) {
          const m = disp.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/i);
          if (m && m[1]) filename = decodeURIComponent(m[1].replace(/['"]/g, ''));
        }

        const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 100);
        }
      } catch (err) {
        console.error('Download failed', err);
        notify.error('Couldn’t download the file. Check your connection and try again.');
      } finally {
        this[spinnerFlag] = false;
      }
    },

    highlight(text) {
      const q = this.searchQuery.trim();
      if (!q || !text) return this.escapeHtml(String(text ?? ''));
      const escaped = this.escapeHtml(String(text));
      const escapedQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return escaped.replace(
        new RegExp(`(${escapedQ})`, 'gi'),
        '<mark class="search-highlight">$1</mark>'
      );
    },

    escapeHtml(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
  }
};
</script>

<style scoped>
.pdf-content-table :deep(thead th) {
  background: #e0f2fe;
  color: #0c4a6e;
}

/* The narrow "Sr" column is a counter, so it centres; everything else reads as
   text and stays left. */
.pdf-content-table :deep(th:first-child),
.pdf-content-table :deep(td:first-child) {
  text-align: center;
}

.oblig-section-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 1rem;
  color: #1e293b;
  margin: 0.5rem 0 0.6rem;
}

.oblig-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.oblig-section-head.contractor .oblig-dot { background: #0f766e; }
.oblig-section-head.client .oblig-dot { background: #7c3aed; }

.oblig-count {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 0.05rem 0.55rem;
}

.due-badge {
  display: inline-block;
  background: #fef3c7;
  color: #92400e;
  font-weight: 600;
  border-radius: 6px;
  padding: 0.1rem 0.5rem;
  white-space: nowrap;
}

.due-none {
  color: #64748b;
  font-size: 0.85rem;
}

.pdf-search-wrapper { position: relative; display: flex; align-items: center; }
.pdf-search-icon { position: absolute; left: 0.75rem; color: #64748b; pointer-events: none; flex-shrink: 0; }
.pdf-search-input {
  padding: 0.5rem 2.2rem 0.5rem 2.2rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  background: #f8fafc;
  color: #1e293b;
  width: 220px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.pdf-search-input:focus {
  border-color: #0071c5;
  box-shadow: 0 0 0 3px rgba(0, 113, 197, 0.12);
  background: #fff;
}
.pdf-search-input::placeholder { color: #64748b; }
.pdf-search-clear {
  position: absolute;
  right: 0.55rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.2s;
}
.pdf-search-clear:hover { color: #ef4444; }

:deep(.search-highlight) {
  background: #fef08a;
  color: #1e293b;
  border-radius: 4px;
  padding: 0 1px;
  font-weight: 600;
}

.spin {
  animation: spin 1s linear infinite;
}

.btn-download.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: #64748b;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
